import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import CustomSelect from 'components/Form/CustomSelect';
import { EmployeeBenefitType } from '../types/on-boarding.type';
import {
  useCreateEmployeeBenefit,
  useDeleteEmployeeBenefit,
  useGetEmployeeBenefitList
} from 'common/queries-and-mutations/employee-benefit';
import { findNextPage, findPreviousPage, showRemoveButton } from 'utils';
import { Loader } from 'components/Loader';
import * as Yup from 'yup';
import { employeeBenefits } from 'constants/employee-benefits';
import { STEPS } from 'constants/steps';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function EmployeeBenefitInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isLoading, isError } = useCreateEmployeeBenefit();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const { mutate: deleteEmployeeBenefit, isLoading: isDeleting } =
    useDeleteEmployeeBenefit();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    data,
    isSuccess: isBenefitSuccess,
    isLoading: isBenefitLoading
  } = useGetEmployeeBenefitList();

  const employerBenefit = useMemo(() => {
    if (isBenefitSuccess && data.length > 0) {
      return data.map(benefit => ({
        employeeBenefitId: benefit.id,
        employerName: benefit.employerName,
        employerAddress: benefit.employerAddress,
        employeeId: benefit.employeeId,
        employeeBenefit: benefit.employeeBenefit
      }));
    } else {
      return [
        {
          employeeBenefitId: null,
          employerName: '',
          employerAddress: '',
          employeeId: '',
          employeeBenefit: ''
        }
      ];
    }
  }, [data, isBenefitSuccess]);

  const formik = useFormik({
    initialValues: {
      employerBenefit
    },
    validationSchema: Yup.object({
      employerBenefit: Yup.array().of(
        Yup.object().shape({
          employerName: Yup.string()
            .required('Employer name is required')
            .nullable(),
          employerAddress: Yup.string().required(
            'Employer address is required'
          ),
          employeeId: Yup.string()
            .required('Employer ID is required')
            .nullable(),
          employeeBenefit: Yup.string().required(
            'Please select type of benefits'
          )
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBenefitSubmit(values);
    }
  });

  const addMoreBenefit = () => {
    formik.values.employerBenefit.push({
      employeeBenefitId: null,
      employerName: '',
      employerAddress: '',
      employeeId: '',
      employeeBenefit: ''
    });
    formik.setFieldValue('employerBenefit', [...formik.values.employerBenefit]);
  };

  const deleteBenefit = (
    indexToRemove: number,
    employeeBenefitId?: number | null
  ) => {
    if (employeeBenefitId) {
      deleteEmployeeBenefit(employeeBenefitId);
    }
    formik.values.employerBenefit.splice(indexToRemove, 1);
    formik.setFieldValue('employerBenefit', [...formik.values.employerBenefit]);
  };

  const onBenefitSubmit = (formValue: EmployeeBenefitType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.EMPLOYEE_BENEFIT_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, dispatch, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(
      findPreviousPage(planAllowPage, STEPS.EMPLOYEE_BENEFIT_INFORMATION)
    );
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.EMPLOYEE_BENEFIT_INFORMATION}
        timelineStep={2}
      />
      {isBenefitLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Financial Asset - Employee Benefits
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.employerBenefit.map((benefit, index) => (
              <div key={index} className="mb-1 mt-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`employerBenefit[${index}].employerName`}
                      placeholder=""
                      value={benefit.employerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Name of Employer"
                      error={
                        formik.touched.employerBenefit
                          ? formik.errors.employerBenefit?.[index] &&
                            formik.errors.employerBenefit.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.employerBenefit[index]
                                )
                              ).employerName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      isRequired
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`employerBenefit[${index}].employerAddress`}
                      placeholder=""
                      value={benefit.employerAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Address of Employer"
                      error={
                        formik.touched.employerBenefit
                          ? formik.errors.employerBenefit?.[index] &&
                            formik.errors.employerBenefit.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.employerBenefit[index]
                                )
                              ).employerAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      isRequired
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`employerBenefit[${index}].employeeId`}
                      placeholder=""
                      value={benefit.employeeId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Employer ID"
                      error={
                        formik.touched.employerBenefit
                          ? formik.errors.employerBenefit?.[index] &&
                            formik.errors.employerBenefit.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.employerBenefit[index]
                                )
                              ).employeeId
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      isRequired
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={employeeBenefits}
                      selectedOption={{
                        value: benefit.employeeBenefit,
                        label: benefit.employeeBenefit
                      }}
                      handleOptionChange={option => {
                        formik.setFieldValue(
                          `employerBenefit[${index}].employeeBenefit`,
                          option?.value
                        );
                      }}
                      label="Select Benefit"
                      name="employeeBenefit"
                      isSearchable
                      className="h-[3.5rem] rounded-2xl"
                      error={
                        formik.touched.employerBenefit
                          ? formik.errors.employerBenefit?.[index] &&
                            formik.errors.employerBenefit.length > 0
                            ? JSON.parse(
                                JSON.stringify(
                                  formik.errors.employerBenefit[index]
                                )
                              ).employeeBenefit
                            : ''
                          : ''
                      }
                      isRequired
                    />
                  </div>
                </div>
                {showRemoveButton(
                  index,
                  benefit.employeeBenefitId,
                  data!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          deleteBenefit(index, benefit.employeeBenefitId)
                        }
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="flex items-center justify-end gap-2">
              <div
                className="flex items-center gap-4"
                onClick={addMoreBenefit}
                role="button"
                tabIndex={-1}
              >
                <Icon name={Icons.AddCircleOutline} />
                <span className="mt-2 text-base font-medium text-[#353535]">
                  Add Another Employee Benefit
                </span>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-1 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-5 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
